import {
  ShotData,
  ShotOptimization,
  OptimalShotParameters
} from '../types';
import {
  TrajectoryCalculator,
  OptimizationEngine,
  SweetSpotAnalyzer,
  calculateEnergyTransfer
} from './physics';

/**
 * Generate comprehensive shot optimization analysis
 */
export class ShotOptimizer {
  private trajectoryCalc: TrajectoryCalculator;
  private optimizationEngine: OptimizationEngine;
  private sweetSpotAnalyzer: SweetSpotAnalyzer;
  
  constructor() {
    this.trajectoryCalc = new TrajectoryCalculator();
    this.optimizationEngine = new OptimizationEngine();
    this.sweetSpotAnalyzer = new SweetSpotAnalyzer();
  }
  
  /**
   * Analyze shot and generate optimization recommendations
   */
  async optimizeShot(shot: ShotData): Promise<ShotOptimization> {
    // Get actual parameters
    const actualParams: OptimalShotParameters = {
      launchAngle: shot.launchAngle,
      exitVelocity: shot.exitVelocity,
      horizontalAngle: shot.horizontalAngle,
      spinRate: shot.spinRate,
      impactLocation: {
        x: shot.impactLocationX,
        y: shot.impactLocationY
      }
    };
    
    // Calculate optimal parameters - keep similar angle but optimize velocity
    // For the same shot type, the trajectory path should be similar, just longer
    const baseOptimal = this.optimizationEngine.findOptimalParameters(
      shot.batSpeed,
      shot.ballSpeed,
      shot.shotType
    );
    
    // Realistic cricket constraints
    const MAX_EXIT_VELOCITY = 55; // m/s - realistic maximum for cricket
    const MIN_IMPROVEMENT = 2; // m/s - minimum meaningful improvement
    
    // Check if the shot is already excellent (distance > 85m or quality is excellent)
    const isAlreadyOptimal = shot.distance >= 85 || shot.quality === 'excellent' || shot.exitVelocity >= 50;
    
    // Calculate optimal exit velocity with realistic constraints
    let optimalExitVelocity: number;
    if (isAlreadyOptimal) {
      // If already good, minimal improvement
      optimalExitVelocity = Math.min(MAX_EXIT_VELOCITY, shot.exitVelocity + 2);
    } else {
      // Calculate realistic improvement based on current technique
      const improvementFactor = shot.quality === 'poor' ? 1.15 : 
                               shot.quality === 'fair' ? 1.1 : 1.05;
      optimalExitVelocity = Math.min(MAX_EXIT_VELOCITY, shot.exitVelocity * improvementFactor);
      
      // Ensure minimum meaningful improvement
      if (optimalExitVelocity - shot.exitVelocity < MIN_IMPROVEMENT) {
        optimalExitVelocity = Math.min(MAX_EXIT_VELOCITY, shot.exitVelocity + MIN_IMPROVEMENT);
      }
    }
    
    // If shot is already optimal, use actual parameters as optimal
    // Otherwise, calculate realistic improvements
    const optimalParams: OptimalShotParameters = isAlreadyOptimal ? {
      launchAngle: shot.launchAngle,
      exitVelocity: shot.exitVelocity,
      horizontalAngle: shot.horizontalAngle,
      spinRate: shot.spinRate,
      impactLocation: {
        x: shot.impactLocationX,
        y: shot.impactLocationY
      }
    } : {
      ...baseOptimal,
      horizontalAngle: shot.horizontalAngle, // Keep same direction
      launchAngle: Math.max(5, Math.min(35, shot.launchAngle + 1)), // Optimize within realistic range
      exitVelocity: optimalExitVelocity, // Realistic improvement
      spinRate: Math.min(3500, shot.spinRate * 1.05), // Slightly better spin, capped at realistic max
      impactLocation: { x: 0, y: 0.15 } // Sweet spot
    };
    
    // Calculate improvements needed
    const improvements = {
      launchAngleDelta: optimalParams.launchAngle - actualParams.launchAngle,
      velocityDelta: optimalParams.exitVelocity - actualParams.exitVelocity,
      horizontalAngleDelta: optimalParams.horizontalAngle - actualParams.horizontalAngle,
      spinRateDelta: optimalParams.spinRate - actualParams.spinRate,
      impactLocationDelta: {
        x: optimalParams.impactLocation.x - actualParams.impactLocation.x,
        y: optimalParams.impactLocation.y - actualParams.impactLocation.y
      }
    };
    
    // Calculate potential distance gain
    const actualTrajectory = this.trajectoryCalc.calculateTrajectory(
      actualParams.exitVelocity,
      actualParams.launchAngle,
      actualParams.horizontalAngle,
      actualParams.spinRate
    );
    
    const optimalTrajectory = this.trajectoryCalc.calculateTrajectory(
      optimalParams.exitVelocity,
      optimalParams.launchAngle,
      optimalParams.horizontalAngle,
      optimalParams.spinRate
    );
    
    // Ensure realistic distance constraints
    const maxRealisticDistance = 120; // meters - world record six is ~158m, but 120m is more typical max
    const constrainedOptimalDistance = Math.min(maxRealisticDistance, optimalTrajectory.totalDistance);
    const constrainedActualDistance = Math.min(maxRealisticDistance, actualTrajectory.totalDistance);
    
    const potentialDistanceGain = constrainedOptimalDistance - constrainedActualDistance;
    
    // Generate recommendations and drill suggestions
    const recommendations = this.generateRecommendations(shot, improvements);
    const drillSuggestions = this.generateDrillSuggestions(shot, improvements);
    
    return {
      actual: actualParams,
      optimal: optimalParams,
      improvements,
      potentialDistanceGain,
      recommendations,
      drillSuggestions
    };
  }
  
  /**
   * Generate specific technique recommendations
   */
  private generateRecommendations(shot: ShotData, improvements: any): string[] {
    const recommendations: string[] = [];
    
    // Launch angle recommendations
    if (Math.abs(improvements.launchAngleDelta) > 2) {
      if (improvements.launchAngleDelta > 0) {
        recommendations.push(`Increase launch angle by ${improvements.launchAngleDelta.toFixed(1)}° - try hitting through the ball more`);
      } else {
        recommendations.push(`Decrease launch angle by ${Math.abs(improvements.launchAngleDelta).toFixed(1)}° - keep the ball down more`);
      }
    }
    
    // Exit velocity recommendations
    if (improvements.velocityDelta > 2) {
      recommendations.push(`Increase bat speed - focus on timing and follow-through for ${improvements.velocityDelta.toFixed(1)} m/s more exit velocity`);
    }
    
    // Horizontal angle recommendations
    if (Math.abs(improvements.horizontalAngleDelta) > 5) {
      const direction = improvements.horizontalAngleDelta > 0 ? 'more to leg side' : 'more to off side';
      recommendations.push(`Adjust shot direction ${direction} by ${Math.abs(improvements.horizontalAngleDelta).toFixed(1)}°`);
    }
    
    // Sweet spot recommendations
    const sweetSpotAnalysis = this.sweetSpotAnalyzer.analyzeSweetSpot(
      shot.impactLocationX,
      shot.impactLocationY
    );
    if (sweetSpotAnalysis.efficiency < 0.8) {
      recommendations.push(sweetSpotAnalysis.recommendation);
    }
    
    // Power transfer recommendations
    if (shot.powerTransfer < 0.8) {
      recommendations.push('Improve bat-ball contact - focus on solid connection and timing');
    }
    
    // Timing recommendations
    if (shot.timing !== 'excellent') {
      recommendations.push('Work on timing - practice watching the ball onto the bat');
    }
    
    // Footwork recommendations
    if (shot.footwork !== 'excellent') {
      recommendations.push('Improve footwork - ensure proper weight transfer and positioning');
    }
    
    // Balance recommendations
    if (shot.balance !== 'excellent') {
      recommendations.push('Focus on balance throughout the shot - maintain steady head position');
    }
    
    // Follow-through recommendations
    if (shot.followThrough !== 'complete') {
      recommendations.push('Complete the follow-through - extend through the shot for maximum power');
    }
    
    return recommendations;
  }
  
  /**
   * Generate specific drill suggestions
   */
  private generateDrillSuggestions(shot: ShotData, improvements: any): string[] {
    const drills: string[] = [];
    
    // Launch angle drills
    if (Math.abs(improvements.launchAngleDelta) > 2) {
      if (improvements.launchAngleDelta > 0) {
        drills.push('Tee work: Practice hitting balls off a tee at waist height to improve upward bat path');
        drills.push('Wall drill: Hit against a wall focusing on getting under the ball');
      } else {
        drills.push('Low tee drill: Practice with tee at knee height to encourage downward angle');
        drills.push('Top-hand drill: One-handed top-hand swings to control bat path');
      }
    }
    
    // Power/timing drills
    if (improvements.velocityDelta > 2 || shot.powerTransfer < 0.8) {
      drills.push('Medicine ball throws: Rotational power development');
      drills.push('Heavy bat swings: Build bat speed and strength');
      drills.push('Reaction drills: Quick decision making and timing practice');
    }
    
    // Sweet spot drills
    const sweetSpotAnalysis = this.sweetSpotAnalyzer.analyzeSweetSpot(
      shot.impactLocationX,
      shot.impactLocationY
    );
    if (sweetSpotAnalysis.efficiency < 0.8) {
      drills.push('Sweet spot marking: Mark the sweet spot and practice hitting that area');
      drills.push('Balance and control: Slow-motion swings focusing on bat control');
    }
    
    // Footwork drills
    if (shot.footwork !== 'excellent') {
      drills.push('Ladder drills: Improve foot speed and coordination');
      drills.push('Shadow batting: Practice footwork without a ball');
      drills.push('Cone drills: Work on precise foot placement');
    }
    
    // Shot-specific drills
    switch (shot.shotType.toLowerCase()) {
      case 'drive':
        drills.push('Straight drive practice: Focus on playing straight with full face');
        break;
      case 'pull':
        drills.push('Pull shot positioning: Practice getting into position quickly');
        drills.push('Short ball machine work: Repetitive pull shot practice');
        break;
      case 'sweep':
        drills.push('Sweep against spin bowling: Practice reading length and line');
        break;
      case 'cut':
        drills.push('Cut shot timing: Practice late cut and square cut timing');
        break;
    }
    
    return drills;
  }
}

/**
 * Calculate shot difficulty score
 */
export function calculateShotDifficulty(shot: ShotData): number {
  let difficulty = 0;
  
  // Ball speed factor (faster balls are harder)
  difficulty += Math.min(40, shot.ballSpeed) / 40 * 0.3;
  
  // Launch angle difficulty (very high or very low angles are harder)
  const idealAngle = 25;
  const angleDiff = Math.abs(shot.launchAngle - idealAngle);
  difficulty += Math.min(20, angleDiff) / 20 * 0.2;
  
  // Horizontal angle difficulty (square shots are harder)
  difficulty += Math.min(90, Math.abs(shot.horizontalAngle)) / 90 * 0.2;
  
  // Sweet spot difficulty (off-center hits are evidence of difficulty)
  difficulty += shot.sweetSpotDistance / 0.05 * 0.2;
  
  // Power transfer difficulty (poor transfer indicates difficulty)
  difficulty += (1 - shot.powerTransfer) * 0.1;
  
  return Math.min(1, difficulty);
}

/**
 * Generate performance insights
 */
export function generatePerformanceInsights(shot: ShotData, optimization: ShotOptimization): string[] {
  const insights: string[] = [];
  
  // Distance potential
  if (optimization.potentialDistanceGain > 10) {
    insights.push(`You could gain ${optimization.potentialDistanceGain.toFixed(1)}m more distance with optimal technique`);
  } else if (optimization.potentialDistanceGain > 5) {
    insights.push(`Small technique adjustments could add ${optimization.potentialDistanceGain.toFixed(1)}m to this shot`);
  } else {
    insights.push('Excellent shot execution - minimal room for improvement');
  }
  
  // Power efficiency
  const energyEfficiency = calculateEnergyTransfer(shot.batSpeed, shot.ballSpeed, shot.exitVelocity);
  if (energyEfficiency < 0.6) {
    insights.push('Focus on timing and contact quality to improve power transfer');
  } else if (energyEfficiency > 0.9) {
    insights.push('Excellent power transfer - great timing and contact');
  }
  
  // Technique assessment
  const techniqueFactors = [shot.timing, shot.footwork, shot.balance, shot.followThrough];
  const excellentCount = techniqueFactors.filter(factor => factor === 'excellent').length;
  
  if (excellentCount === 4) {
    insights.push('Perfect technique execution across all fundamentals');
  } else if (excellentCount >= 2) {
    insights.push('Good fundamental technique with room for refinement');
  } else {
    insights.push('Focus on fundamental technique development');
  }
  
  // Shot type specific insights
  switch (shot.shotType.toLowerCase()) {
    case 'drive':
      if (shot.launchAngle > 25) {
        insights.push('For drives, try to keep the ball along the ground more');
      }
      break;
    case 'pull':
      if (shot.horizontalAngle < 20) {
        insights.push('Pull shots work best when hit square or behind square');
      }
      break;
    case 'sweep':
      if (shot.launchAngle > 20) {
        insights.push('Sweeps are most effective when kept low');
      }
      break;
  }
  
  return insights;
}